var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { NodeComponentType, TubeType, } from "./types.current";
import { systemDataConstants } from "api/system-data";
import { tubeDiameterOptionInitialIndex, tubeDiameterOptions, } from "store/types";
import { tubeDefaultLength } from "3d/constants/common";
export var getInitialTubeDiameterIndex = function (unitSystem, systemData) {
    var systemType = systemData.parameters[systemDataConstants.parameterNames.vacuumSystemType]
        .value === systemDataConstants.parameterValues.centralizedSystem
        ? "centralized"
        : "decentralized";
    return tubeDiameterOptionInitialIndex[unitSystem][systemType];
};
export var addEdgeToState = function (state, payload) {
    var defaultDiameterIndex = payload.initialTubeDiameterIndex;
    var blowOffDiameterIndex = payload.initialTubeDiameterIndex;
    state.edges[payload.edgeId] = {
        id: payload.edgeId,
        diameterDefaultTube: tubeDiameterOptions[payload.unitSystem][defaultDiameterIndex].value,
        diameterBlowOffTube: tubeDiameterOptions[payload.unitSystem][blowOffDiameterIndex].value,
        defaultDiameterIndex: defaultDiameterIndex,
        blowOffDiameterIndex: blowOffDiameterIndex,
        length: tubeDefaultLength,
        tubeType: TubeType.UNKNOWN,
        fromAttachmentId: payload.fromAttachmentId,
        toAttachmentId: payload.toAttachmentId,
    };
    state.attachments[payload.fromAttachmentId].edgeId = payload.edgeId;
    state.attachments[payload.toAttachmentId].edgeId = payload.edgeId;
};
export var updateTubeTypes = function (state) {
    var e_1, _a;
    var traversed = new Set();
    var notTraversed = new Set();
    var recursiveUpdateTubes = function (currentNode, currentType) {
        /* If the current node is a tube connection, update the type of that connection*/
        if (currentNode.type === NodeComponentType.TUBE_CONNECTION) {
            var tubeConnection = currentNode.component;
            tubeConnection.tubeType = currentType;
            currentNode.attachmentIds.forEach(function (attachmentId) {
                var attachment = state.attachments[attachmentId];
                attachment.tubeType = currentType;
            });
        }
        /* Look at all attachments for this node, if the attachment has an edge that
         * we have not yet traversed, update it's type and recursively call this
         * function for the node that the edge leads to */
        currentNode.attachmentIds.forEach(function (attachmentId) {
            var attachment = state.attachments[attachmentId];
            if (attachment.edgeId && !traversed.has(attachment.edgeId)) {
                /* The tube type changes when we encounter a vacuum pump. If the current
                 * attachment is the back of the pump (leading to the feed pressure point),
                 * the type of the edge and any nodes/edges that edge is connected to will be
                 * "compressed_air". If it's the front of the pump it will be "vacuum" */
                var changedType = null;
                if (currentNode.type === NodeComponentType.CENTRALIZED_VACUUM_PUMP) {
                    changedType = attachment.tubeType;
                }
                var currentEdge = state.edges[attachment.edgeId];
                currentEdge.tubeType = changedType || currentType;
                var otherAttachment = currentEdge.fromAttachmentId === attachmentId
                    ? state.attachments[currentEdge.toAttachmentId]
                    : state.attachments[currentEdge.fromAttachmentId];
                var otherNode = state.nodes[otherAttachment.nodeId];
                traversed.add(attachment.edgeId);
                recursiveUpdateTubes(otherNode, changedType || currentType);
            }
        });
    };
    /* Start the traversal from the feed pressure point and with type "compressed_air"*/
    recursiveUpdateTubes(state.nodes["feed-pressure-point"], TubeType.COMPRESSED_AIR);
    try {
        /* The network may contain node and edges that are not yet connected to the feed
         * pressure point. These should all have type "unknown" */
        /* Go through all edges, if they have not been traversed in the recursive function
         * they should have type "unknown".
         * However the tube connections connected to the edges might have a leaf node with a set tubeType.
         * To figure out if that is the case, we recursively travel across all edges to find a disconnected cup/pump
         * which will determine the tubeType of the disconnected tube connection and all its attachments */
        for (var _b = __values(Object.values(state.edges)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var edge = _c.value;
            if (traversed.has(edge.id))
                continue;
            notTraversed.add(edge.id);
            var fromAttachment = state.attachments[edge.fromAttachmentId];
            var fromNode = state.nodes[fromAttachment.nodeId];
            var toAttachment = state.attachments[edge.toAttachmentId];
            var toNode = state.nodes[toAttachment.nodeId];
            var tubeType = recursivelyLookForTubeType(state, fromAttachment, new Set());
            // set the tube type for this edge and for the attachments it connects to
            edge.tubeType = tubeType;
            state.attachments[edge.fromAttachmentId].tubeType = tubeType;
            state.attachments[edge.toAttachmentId].tubeType = tubeType;
            // if the connected nodes are TubeConnectors, set the type on these as well
            if (fromNode.component.tubeType) {
                setTubeConnectionType(state, fromNode, tubeType);
            }
            if (toNode.component.tubeType) {
                setTubeConnectionType(state, toNode, tubeType);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    resetCompletetlyDisconnectedTubeConnections(state);
    state.hasDisconnectedEdges = notTraversed.size > 0;
    return state;
};
// look at all connected nodes and see if we can find a node that is not a Tube Connection and that has a known type
var recursivelyLookForTubeType = function (state, attachment, traversedEdges) {
    var e_2, _a;
    var node = state.nodes[attachment.nodeId];
    var attachmentsToInvestigate = node.type === NodeComponentType.TUBE_CONNECTION
        ? node.attachmentIds
        : [attachment.id];
    try {
        for (var attachmentsToInvestigate_1 = __values(attachmentsToInvestigate), attachmentsToInvestigate_1_1 = attachmentsToInvestigate_1.next(); !attachmentsToInvestigate_1_1.done; attachmentsToInvestigate_1_1 = attachmentsToInvestigate_1.next()) {
            var attachmentId = attachmentsToInvestigate_1_1.value;
            var attachment_1 = state.attachments[attachmentId];
            if (attachment_1.tubeType !== TubeType.UNKNOWN &&
                node.type !== NodeComponentType.TUBE_CONNECTION) {
                // found an attachment with known tube type
                return attachment_1.tubeType;
            }
            if (!attachment_1.edgeId || traversedEdges.has(attachment_1.edgeId))
                continue;
            traversedEdges.add(attachment_1.edgeId);
            var edge = state.edges[attachment_1.edgeId];
            var nextAttachment = edge.fromAttachmentId === attachment_1.id
                ? state.attachments[edge.toAttachmentId]
                : state.attachments[edge.fromAttachmentId];
            var tubeType = recursivelyLookForTubeType(state, nextAttachment, traversedEdges);
            if (tubeType !== TubeType.UNKNOWN)
                return tubeType;
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (attachmentsToInvestigate_1_1 && !attachmentsToInvestigate_1_1.done && (_a = attachmentsToInvestigate_1.return)) _a.call(attachmentsToInvestigate_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return TubeType.UNKNOWN;
};
/* If a node is completely disconnected and has no edges it should have type "unknown" */
var resetCompletetlyDisconnectedTubeConnections = function (state) {
    var e_3, _a;
    try {
        for (var _b = __values(Object.values(state.nodes)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var node = _c.value;
            if (node.type !== NodeComponentType.TUBE_CONNECTION)
                continue;
            var hasEdgeConnections = node.attachmentIds.some(function (attachmentId) { return state.attachments[attachmentId].edgeId; });
            if (hasEdgeConnections)
                continue;
            setTubeConnectionType(state, node, TubeType.UNKNOWN);
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_3) throw e_3.error; }
    }
};
var setTubeConnectionType = function (state, node, type) {
    var tubeConnection = node.component;
    tubeConnection.tubeType = type;
    node.attachmentIds.forEach(function (attachmentId) {
        var attachment = state.attachments[attachmentId];
        attachment.tubeType = type;
    });
};
export var defaultNodeWidth = 0.1;
export var findFreeNodePosition = function (newPosition, existingNodes, type) {
    var e_4, _a, e_5, _b;
    var occupiedPositions = [];
    try {
        for (var _c = __values(Object.values(existingNodes)), _d = _c.next(); !_d.done; _d = _c.next()) {
            var node = _d.value;
            // We ignore nodes of different types as they don't occlude the visuals when overlapping.
            if (node.type !== type) {
                continue;
            }
            var width = node.type === NodeComponentType.GRIP_POSITION
                ? node.component.width
                : defaultNodeWidth;
            occupiedPositions.push({
                position: node.position,
                width: width,
            });
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_4) throw e_4.error; }
    }
    var isPositionOccupied = function (newNode, existingNodes) {
        var e_6, _a;
        try {
            for (var existingNodes_1 = __values(existingNodes), existingNodes_1_1 = existingNodes_1.next(); !existingNodes_1_1.done; existingNodes_1_1 = existingNodes_1.next()) {
                var existing = existingNodes_1_1.value;
                var dx = Math.abs(newNode.position.x - existing.position.x);
                var dz = Math.abs(newNode.position.z - existing.position.z);
                var overlapX = dx < (newNode.width + existing.width) / 2;
                var overlapZ = dz < (newNode.width + existing.width) / 2;
                if (overlapX && overlapZ)
                    return true;
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (existingNodes_1_1 && !existingNodes_1_1.done && (_a = existingNodes_1.return)) _a.call(existingNodes_1);
            }
            finally { if (e_6) throw e_6.error; }
        }
        return false;
    };
    if (!isPositionOccupied(newPosition, occupiedPositions)) {
        return newPosition.position;
    }
    var directions = [
        { x: 1, z: 0 },
        { x: -1, z: 0 },
        { x: 0, z: 1 },
        { x: 0, z: -1 },
        { x: 1, z: 1 },
        { x: -1, z: 1 },
        { x: 1, z: -1 },
        { x: -1, z: -1 },
    ];
    var attempts = 0;
    var queue = [newPosition.position];
    var visited = new Set();
    var key = function (v) { return "".concat(v.x, ",").concat(v.z); };
    var maxTries = 60;
    while (queue.length > 0 && attempts < maxTries) {
        var current = queue.shift();
        try {
            for (var directions_1 = (e_5 = void 0, __values(directions)), directions_1_1 = directions_1.next(); !directions_1_1.done; directions_1_1 = directions_1.next()) {
                var dir = directions_1_1.value;
                var candidate = {
                    x: current.x + dir.x * newPosition.width,
                    y: newPosition.position.y, // Keep same Y
                    z: current.z + dir.z * newPosition.width,
                };
                if (visited.has(key(candidate)))
                    continue;
                visited.add(key(candidate));
                var testNode = { position: candidate, width: newPosition.width };
                if (!isPositionOccupied(testNode, occupiedPositions)) {
                    return candidate;
                }
                queue.push(candidate);
                attempts++;
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (directions_1_1 && !directions_1_1.done && (_b = directions_1.return)) _b.call(directions_1);
            }
            finally { if (e_5) throw e_5.error; }
        }
    }
    return null; // No free position found
};
